import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import styles from "./SearchPagination.module.scss";
import Chevron from "@Icons/Chevron";
import { getSearchLink } from "@Pages/Search/data/helper";
import wrapperHOC from "@Utils/wrapperHOC";
import { useSelector, shallowEqual } from "react-redux";
import { AppState } from "@Store/index";
import { useCallback } from "react";
import { getFiltersStateActiveStatus } from "@Pages/Search/utils/getFilterActiveStatus";
import { isDemoWhitelabelSubdomain } from "constants/index";
import useIsApp from "hooks/useIsApp";

const ITEMS_PER_PAGE = 10;

const isPaginatedUrl = (): boolean => {
  if (typeof window === "undefined") return false;
  const searchParams = new URLSearchParams(window.location.search);
  const p = searchParams.get("p");
  return !!p && parseInt(p, 10) > 1;
};

const getPageFromUrl = (): number => {
  if (typeof window === "undefined") return 1;
  const searchParams = new URLSearchParams(window.location.search);
  const p = searchParams.get("p");
  return p ? parseInt(p, 10) || 1 : 1;
};

const getPageHref = (canonicalName: string, targetPage: number) => {
  const baseUrl = getSearchLink(canonicalName, {});
  if (targetPage <= 1) return baseUrl;
  return `${baseUrl}?p=${targetPage}`;
};

const appendWhitelabelParams = (url: string, partnerId: string): string =>
  url.includes("?") ? `${url}&partner_id=${partnerId}` : `${url}?partner_id=${partnerId}`;

const getPageNumbers = (activePage: number, totalPages: number): (number | "ellipsis")[] => {
  const pages: (number | "ellipsis")[] = [];
  const siblingsCount = 1;

  const leftSibling = Math.max(activePage - siblingsCount, 1);
  const rightSibling = Math.min(activePage + siblingsCount, totalPages);

  const showLeftEllipsis = leftSibling > 3;
  const showRightEllipsis = rightSibling < totalPages - 2;

  pages.push(1);

  if (showLeftEllipsis) {
    if (leftSibling === 4) {
      pages.push(2, 3);
    } else {
      pages.push(2, "ellipsis");
    }
  } else {
    for (let i = 2; i < leftSibling; i++) {
      pages.push(i);
    }
  }

  for (let i = leftSibling; i <= rightSibling; i++) {
    if (!pages.includes(i)) {
      pages.push(i);
    }
  }

  if (showRightEllipsis) {
    if (rightSibling === totalPages - 3) {
      pages.push(totalPages - 2, totalPages - 1);
    } else {
      pages.push("ellipsis", totalPages - 1);
    }
  } else {
    for (let i = rightSibling + 1; i < totalPages; i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }
  }

  if (!pages.includes(totalPages)) {
    pages.push(totalPages);
  }

  return pages;
};

const SearchPagination = (): JSX.Element | null => {
  const isApp = useIsApp();
  const { meta, region, filters, itemsCount, ssrPage, isRadiusExpanded } = useSelector(
    ({ searchPageData }: AppState) => ({
      meta: searchPageData.searchList.meta,
      region: searchPageData.region,
      filters: searchPageData.filters,
      itemsCount: searchPageData.searchList.items?.length || 0,
      ssrPage: searchPageData.searchList.page || 1,
      isRadiusExpanded: searchPageData.isRadiusExpanded,
    }),
    shallowEqual,
  );

  const { isWhitelabel, subdomain, partner_uuid } = useSelector(
    ({ whitelabelData }: AppState) => ({
      isWhitelabel: whitelabelData.isWhitelabel,
      subdomain: whitelabelData.subdomain,
      partner_uuid: whitelabelData.partner_uuid,
    }),
    shallowEqual,
  );

  const isDemoWhitelabel = isWhitelabel && isDemoWhitelabelSubdomain(subdomain);

  const canonicalName = region?.canonical_name || "";
  const totalPages = meta?.count ? Math.ceil(meta.count / ITEMS_PER_PAGE) : 1;
  // for SSR, use page from Redux and for client, use URL.
  const isPaginated = __CLIENT__ ? isPaginatedUrl() : ssrPage > 1;
  const paginatedPage = __CLIENT__ ? getPageFromUrl() : ssrPage;
  const scrollPage = Math.ceil(itemsCount / ITEMS_PER_PAGE) || 1;
  const activePage = isPaginated ? paginatedPage : scrollPage;
  const areFiltersApplied = getFiltersStateActiveStatus(filters) || isRadiusExpanded;

  const handlePageChange = useCallback(
    (targetPage: number) => {
      if (targetPage < 1 || targetPage > totalPages || targetPage === activePage) return;
      const baseUrl = getSearchLink(canonicalName, {});
      let newUrl = targetPage <= 1 ? baseUrl : `${baseUrl}?p=${targetPage}`;
      if (isDemoWhitelabel && partner_uuid) {
        newUrl = appendWhitelabelParams(newUrl, partner_uuid);
      }
      window.location.href = newUrl;
    },
    [totalPages, activePage, canonicalName, isDemoWhitelabel, partner_uuid],
  );

  // Native / in-app webview uses infinite scroll — hide page controls.
  if (isApp) return null;

  // Hide pagination when filters are applied
  if (areFiltersApplied) {
    if (isPaginated && typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("p");
      window.history.replaceState({}, "", url.toString());
    }
    return null;
  }

  if (totalPages <= 1) return null;

  const handleClick = (e: React.MouseEvent, targetPage: number) => {
    e.preventDefault();
    if (targetPage >= 1 && targetPage <= totalPages) {
      handlePageChange(targetPage);
    }
  };

  const pageItems = getPageNumbers(activePage || 1, totalPages);

  const getFullPageHref = (targetPage: number) => {
    const href = getPageHref(canonicalName, targetPage);
    if (isDemoWhitelabel && partner_uuid) {
      return appendWhitelabelParams(href, partner_uuid);
    }
    return href;
  };

  return (
    <div className={styles.paginationWrapper}>
      <nav
        className={styles.paginationContainer}
        aria-label="Property listing pages"
        data-testid="search-pagination"
      >
        {activePage > 1 && (
          <a
            href={getFullPageHref(activePage - 1)}
            className={styles.controlButton}
            onClick={(e) => handleClick(e, activePage - 1)}
            aria-label="Previous page"
            rel="prev"
          >
            <Chevron rotate="90" height="12" width="12" />
          </a>
        )}

        {pageItems.map((item, idx) =>
          item === "ellipsis" ? (
            <span key={`ellipsis-${idx}`} className={styles.ellipsis}>
              …
            </span>
          ) : (
            <a
              key={item}
              href={getFullPageHref(item)}
              className={formatClassNames(
                styles.pageButton,
                item === activePage ? styles.active : "",
              )}
              onClick={(e) => handleClick(e, item)}
              aria-label={`Page ${item}`}
              aria-current={item === activePage ? "page" : undefined}
            >
              {item}
            </a>
          ),
        )}

        {activePage < totalPages && (
          <a
            href={getFullPageHref(activePage + 1)}
            className={styles.controlButton}
            onClick={(e) => handleClick(e, activePage + 1)}
            aria-label="Next page"
            rel="next"
          >
            <Chevron rotate="270" height="12" width="12" />
          </a>
        )}
      </nav>
    </div>
  );
};

export default wrapperHOC(SearchPagination, {
  componentName: "SearchPagination-SearchPage",
  showForChina: true,
});
